import { useMemo, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { BsExclamationCircle, BsInfoCircle } from 'react-icons/bs';
import { useNavigate, useParams } from 'react-router-dom';

import { ButtonSplitGroup } from '@components/Buttons';
import { BootstrapTable } from '@components/Table/Table';
import { cartApi } from '@reducers/order';

const renderDidsAmountCell = ({
  row,
  initialAmount,
  backorderAmount,
  setBackorderAmount,
}) => {
  const props = {};
  const { stock } = row.original;
  const pending = initialAmount || 0;
  if (stock) {
    props.max = stock - pending;
  }
  return (
    <Form.Control
      type="number"
      placeholder="0"
      min={0}
      onChange={(e) => setBackorderAmount(e.target.value)}
      value={backorderAmount}
      /* eslint-disable-next-line react/jsx-props-no-spreading */
      {...props}
    />
  );
};

const AddBackordersButton = ({ amount, initialAmount, addTrigger }) => {
  const { id, type, prefixID } = useParams();
  const navigate = useNavigate();
  const inputChanged = Number(amount) > 0;
  const handleAddBackorders = () => {
    const continuePath = `/order/numbers/plans/${id}/type/${type}/prefixes/${prefixID}/checkout`;
    if (inputChanged) {
      addTrigger({ amount, plan: id, prefixID, type })
        .unwrap()
        .then(() => navigate(continuePath));
      return;
    }
    navigate(continuePath);
  };
  const hideButtons =
    (Number(amount) <= 0 && !initialAmount) || Number(initialAmount) === 0;
  if (hideButtons) {
    return null;
  }

  return (
    <Button onClick={handleAddBackorders} variant="didxl" size="sm">
      {inputChanged ? 'Add and continue' : 'Continue'}
    </Button>
  );
};

export const BackorderNumbersTable = ({ data, cartData }) => {
  const { prefixID } = useParams();
  const [addTrigger] = cartApi.useAddBackorderToCartMutation();
  const [removeTrigger] = cartApi.useRemoveBackorderFromCartMutation();
  const { id: backorderID, amount: initialAmount } =
    cartData && cartData.length > 0 ? cartData[0] : {};
  const [backorderAmount, setBackorderAmount] = useState(0);
  const navigate = useNavigate();
  const pendingBackordersColumn = initialAmount
    ? [
        {
          id: 'pending',
          Header: '#Pending',
          className: 'col-md-1 text-center',
          Cell: () => initialAmount,
        },
      ]
    : [];
  const columns = useMemo(
    () => [
      {
        Header: 'Country',
        className: 'col-md-1',
        accessor: 'country',
      },
      {
        id: 'lt',
        Header: 'LT',
        accessor: 'lt_available',
        className: 'col-md-1',
      },
      {
        id: 'pstn',
        Header: 'PSTN',
        accessor: 'pstn_available',
        className: 'col-md-1',
      },
      {
        id: 'sms',
        Header: 'SMS',
        accessor: 'sms_available',
        className: 'col-md-1',
      },
      {
        Header: 'City/ Area Code',
        className: 'col-md-2',
        accessor: 'area',
      },
      {
        Header: 'Plan',
        className: 'col-md-1 text-center',
        accessor: 'plan.name',
      },
      {
        id: 'didsAmount',
        Header: `#DID's`,
        className: 'col-md-1',
        Cell: ({ row }) =>
          renderDidsAmountCell({
            row,
            initialAmount,
            backorderAmount,
            setBackorderAmount,
          }),
      },
      ...pendingBackordersColumn,
    ],
    [backorderAmount]
  );
  return (
    <Card>
      <Card.Header className="bg-didxl text-white" />
      <Card.Body>
        <BootstrapTable
          columns={columns}
          data={data}
          tableProps={() => ({
            responsive: true,
            bordered: true,
            className: 'normalFontSize mt-2',
          })}
          hiddenColumns="id"
          tableHeader="Please specify the #DIDs you would like to order:"
        />
        <ButtonSplitGroup>
          <Button
            onClick={() => {
              if (backorderID) {
                removeTrigger({ id: backorderID, prefixID })
                  .unwrap()
                  .then(() => {
                    navigate('/order/numbers');
                  });
                return;
              }
              navigate('/order/numbers');
            }}
            variant="didxl"
            size="sm"
          >
            Cancel
          </Button>
          <AddBackordersButton
            addTrigger={addTrigger}
            amount={backorderAmount}
            initialAmount={initialAmount}
          />
        </ButtonSplitGroup>
        <Row className="mt-2">
          <Col>
            <BsExclamationCircle size={17} /> The backorders price depends on
            the availability of the supplier and which type / area / etc is
            finally chosen. If price differs from the price in pricelist, you
            will be contacted by our support team.
          </Col>
        </Row>
        <Row>
          <Col>
            <BsInfoCircle size={17} /> For number range, consecutive numbers,
            missing area&apos;s or missing countries please drop an email to
            sales@didxl.com.
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};
